import { isReloadingAfterPreloadError } from './preloadReloadState';

type SentryModule = typeof import('@sentry/react');
type CaptureExceptionOptions = Parameters<SentryModule['captureException']>[1];
type CaptureMessageOptions = Parameters<SentryModule['captureMessage']>[1];
type Breadcrumb = Parameters<SentryModule['addBreadcrumb']>[0];
type User = Parameters<SentryModule['setUser']>[0];
type ReactErrorHandlerCallback = Parameters<SentryModule['reactErrorHandler']>[0];
type ReactErrorHandler = ReturnType<SentryModule['reactErrorHandler']>;
type Integration = Parameters<SentryModule['addIntegration']>[0];

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const SENTRY_APPLICATION_KEY = import.meta.env.VITE_SENTRY_APPLICATION_KEY;
const isDevelopment = import.meta.env.DEV;
const enableInDev = import.meta.env.VITE_SENTRY_ENABLE_IN_DEV === 'true';
const shouldInitialize = Boolean(SENTRY_DSN) && (!isDevelopment || enableInDev);

let isInitialized = false;
let sentryModule: SentryModule | null = null;
let sentryModulePromise: Promise<SentryModule> | null = null;
let initSentryPromise: Promise<SentryModule | null> | null = null;

function parseReplaySampleRate(value: string | undefined): number {
  if (!value?.trim()) {
    return 1.0;
  }

  const rate = Number(value);
  return Number.isFinite(rate) && rate >= 0 && rate <= 1 ? rate : 1.0;
}

function thirdPartyErrorFilterIntegrations(module: SentryModule): Integration[] {
  if (!SENTRY_APPLICATION_KEY) {
    return [];
  }

  return [
    module.thirdPartyErrorFilterIntegration({
      filterKeys: [SENTRY_APPLICATION_KEY],
      behaviour: 'drop-error-if-exclusively-contains-third-party-frames'
    })
  ];
}

function loadSentry(): Promise<SentryModule> {
  if (sentryModule) {
    return Promise.resolve(sentryModule);
  }

  sentryModulePromise ??= import('@sentry/react').then((module) => {
    sentryModule = module;
    return module;
  });

  return sentryModulePromise;
}

async function initializeSentry(enableReplay: boolean): Promise<SentryModule | null> {
  if (!shouldInitialize) {
    return null;
  }

  if (isInitialized && sentryModule) {
    return sentryModule;
  }

  const module = await loadSentry();

  if (isInitialized) {
    return module;
  }

  module.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'production',
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    beforeSend: (event) => (isReloadingAfterPreloadError() ? null : event),
    replaysSessionSampleRate: enableReplay
      ? parseReplaySampleRate(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE)
      : 0,
    replaysOnErrorSampleRate: enableReplay
      ? parseReplaySampleRate(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE)
      : 0,
    integrations: [
      module.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
      ...thirdPartyErrorFilterIntegrations(module),
      ...(enableReplay
        ? [
            module.replayIntegration({
              maskAllText: false,
              maskAllInputs: true,
              blockAllMedia: false
            })
          ]
        : [])
    ]
  });

  isInitialized = true;

  return module;
}

function withSentry(callback: (module: SentryModule) => void): void {
  if (!shouldInitialize) {
    return;
  }

  void initSentry().then((module) => {
    if (module) {
      callback(module);
    }
  });
}

export async function initSentry({
  enableReplay = true
}: { enableReplay?: boolean } = {}): Promise<SentryModule | null> {
  initSentryPromise ??= initializeSentry(enableReplay).catch(() => {
    initSentryPromise = null;
    return null;
  });

  return initSentryPromise;
}

export const Sentry = {
  captureException(error: unknown, options?: CaptureExceptionOptions): void {
    withSentry((module) => {
      module.captureException(error, options);
    });
  },

  captureMessage(message: string, options?: CaptureMessageOptions): void {
    withSentry((module) => {
      module.captureMessage(message, options);
    });
  },

  addBreadcrumb(breadcrumb: Breadcrumb): void {
    withSentry((module) => {
      module.addBreadcrumb(breadcrumb);
    });
  },

  setUser(user: User): void {
    withSentry((module) => {
      module.setUser(user);
    });
  },

  reactErrorHandler(callback?: ReactErrorHandlerCallback): ReactErrorHandler {
    if (isInitialized && sentryModule) {
      return sentryModule.reactErrorHandler(callback);
    }

    return (error, errorInfo) => {
      callback?.(error, errorInfo, '');

      withSentry((module) => {
        module.reactErrorHandler()(error, errorInfo);
      });
    };
  }
};
